import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeftIcon, UserCircleIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../context/AuthContext'
import { authClient } from '../auth/betterAuthClient'

// Operators sign in to their own Better Auth instance (ops_* tables, see
// betterAuthClient.ts); name and password changes go straight to it.
const MIN_PASSWORD = 12

function errorText(err: { code?: string; message?: string; status?: number } | null | undefined, fallback: string): string {
  if (!err) return fallback
  if (err.code === 'INVALID_PASSWORD') return 'Your current password is not right.'
  if (err.code === 'PASSWORD_TOO_SHORT') return `Use at least ${MIN_PASSWORD} characters.`
  if (err.code === 'PASSWORD_TOO_LONG') return 'That password is too long.'
  if (err.status === 401) return 'Your session ended. Sign in again, then try once more.'
  return err.message || fallback
}

export default function CustomAccountSettings() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const [formData, setFormData] = useState({
    displayName: user?.name || '',
    primaryEmail: user?.email || '',
  })

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })

  useEffect(() => {
    if (!user) {
      navigate('/settings')
    }
  }, [user, navigate])

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    const name = formData.displayName.trim()
    if (!name) {
      setMessage({ type: 'error', text: 'Enter a name.' })
      return
    }
    setLoading(true)
    setMessage(null)
    try {
      const { error } = await authClient.updateUser({ name })
      if (error) throw error
      setMessage({ type: 'success', text: 'Name saved.' })
    } catch (err) {
      setMessage({ type: 'error', text: errorText(err as { code?: string; message?: string }, "Couldn't save your name.") })
    } finally {
      setLoading(false)
    }
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: "The new passwords don't match." })
      return
    }
    if (passwordData.newPassword.length < MIN_PASSWORD) {
      setMessage({ type: 'error', text: `Use at least ${MIN_PASSWORD} characters.` })
      return
    }
    if (passwordData.newPassword === passwordData.currentPassword) {
      setMessage({ type: 'error', text: 'Pick a password different from the current one.' })
      return
    }
    setLoading(true)
    setMessage(null)
    try {
      // Signs every OTHER device out; this tab keeps going on the new session
      // token the client picks up from the response (set-auth-token).
      const { error } = await authClient.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        revokeOtherSessions: true,
      })
      if (error) throw error
      setMessage({ type: 'success', text: 'Password changed. Other devices were signed out.' })
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
    } catch (err) {
      setMessage({ type: 'error', text: errorText(err as { code?: string; message?: string; status?: number }, "Couldn't change your password.") })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center gap-4">
          <button onClick={() => navigate('/settings')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors">
            <ArrowLeftIcon className="w-4 h-4" /> Back
          </button>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <UserCircleIcon className="w-7 h-7 text-blue-600" />
            Account Settings
          </h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-6 space-y-6">
        {message && (
          <div className={`flex items-center gap-3 p-4 rounded-lg text-sm ${
            message.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {message.type === 'success' ? <CheckCircleIcon className="w-5 h-5 flex-shrink-0" /> : <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />}
            {message.text}
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-slate-200">
          {(['profile', 'password'] as const).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-5 py-3 text-sm font-medium transition-colors border-b-2 -mb-px capitalize ${activeTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
              {tab === 'password' ? 'Change Password' : tab}
            </button>
          ))}
        </div>

        {/* Profile */}
        {activeTab === 'profile' && (
          <form onSubmit={handleUpdateProfile} className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-5">
            <h2 className="text-lg font-semibold text-slate-900">Profile Information</h2>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Display Name</label>
              <input type="text" value={formData.displayName} onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
              <input type="email" value={formData.primaryEmail} disabled
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-400 cursor-not-allowed" />
              <p className="text-xs text-slate-400 mt-1">To change your email, contact support</p>
            </div>
            <button type="submit" disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 shadow-sm transition-colors disabled:opacity-50">
              {loading ? 'Saving…' : 'Save Changes'}
            </button>
          </form>
        )}

        {/* Password */}
        {activeTab === 'password' && (
          <form onSubmit={handleChangePassword} className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-5">
            <h2 className="text-lg font-semibold text-slate-900">Change Password</h2>
            <p className="text-sm text-slate-500">At least {MIN_PASSWORD} characters. Changing it signs you out on your other devices.</p>
            {[{ key: 'currentPassword', label: 'Current password', auto: 'current-password' }, { key: 'newPassword', label: 'New password', auto: 'new-password' }, { key: 'confirmPassword', label: 'Confirm new password', auto: 'new-password' }].map(field => (
              <div key={field.key}>
                <label htmlFor={`pw-${field.key}`} className="block text-sm font-medium text-slate-700 mb-1.5">{field.label}</label>
                <input id={`pw-${field.key}`} type="password" autoComplete={field.auto} value={passwordData[field.key as keyof typeof passwordData]}
                  onChange={(e) => setPasswordData({ ...passwordData, [field.key]: e.target.value })} required
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
              </div>
            ))}
            <button type="submit" disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 shadow-sm transition-colors disabled:opacity-50">
              {loading ? 'Changing…' : 'Change Password'}
            </button>
          </form>
        )}

      </div>
    </div>
  )
}
